import React from 'react'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import { Button } from 'transfix-ui/components/Button'
import { Display } from 'transfix-ui/components/Display'
import { DisplayValue } from 'transfix-ui/components/DisplayValue'
import { DraggableListItem, IDraggableListItemProps } from 'transfix-ui/components/DraggableList'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { capitalize } from 'utils/capitalize'
import { IOrderedShipmentStopFragment } from '../../graphql/OrderedShipmentStop'
import { getAppointmentTime } from '../../utils/getAppointmentTime'
import { getFormattedAddress } from '../../utils/getFormattedAddress'

/**
 * Use array index to determine new stopSequence
 */
const getStopSequence = (stopArrayIndex: number): number => {
  return stopArrayIndex + 1
}

interface IShipmentStopListItemProps
  extends Omit<IDraggableListItemProps, 'children' | 'draggableId'> {
  stop: IOrderedShipmentStopFragment
  /**
   * @default false
   */
  isDeleteHidden?: boolean
  onDeleteStop: (shipmentStopId: string) => Promise<void>
}

export const ShipmentStopListItem = ({
  index,
  isDeleteHidden = false,
  isDragDisabled = false,
  onDeleteStop,
  stop,
  ...rest
}: IShipmentStopListItemProps) => {
  const isDeleteEnabled = useFeatureFlag('deleteShipmentStops')
  const { appointment, facility, id, stopType } = stop || {}
  const { location, name } = facility || {}
  const { endAt, startAt, timezone } = appointment || {}
  const addressFormatted = getFormattedAddress(location)
  const nameFormatted = capitalize(name)
  const stopTypeFormatted = capitalize(stopType)
  const stopSequence = getStopSequence(index)
  const appointmentTimeFormatted = getAppointmentTime(startAt, endAt, timezone)

  const deleteStopHandler = () => {
    onDeleteStop(id)
  }

  return (
    <DraggableListItem
      index={index}
      draggableId={id.toString()}
      isDragDisabled={isDragDisabled}
      {...rest}>
      <Box display='flex' width='100%' alignItems='flex-start' justifyContent='space-between'>
        <Box flex='0 0 auto' mr={4} height='100%' display='flex' alignSelf='center'>
          <DisplayValue>{stopSequence}</DisplayValue>
        </Box>
        <Box flex='0 0 auto' mr={4} mb={-2}>
          <Display label='Stop Type'>
            <Box display='flex' alignItems='center' mt={0.5}>
              <Box mr={1}>
                <MapMarker markerStyle='newWave' type={stopType} sequence={stopSequence} />
              </Box>
              <Typography variant='body2' component='div'>
                {stopTypeFormatted}
              </Typography>
            </Box>
          </Display>
        </Box>
        <Box flex='1 1 auto' mr={3} mb={-2}>
          <Display label='Facility'>
            <Typography variant='body2' component='div'>
              {nameFormatted}
            </Typography>
            <Typography variant='caption' component='div' color='textSecondary'>
              {addressFormatted}
            </Typography>
          </Display>
        </Box>
        <Box flex='1 1 auto' mr={0} mb={-2}>
          <Display label='Appointment'>
            <Typography variant='body2' component='div'>
              {appointmentTimeFormatted}
            </Typography>
          </Display>
        </Box>
        {!isDeleteHidden && isDeleteEnabled && (
          <Box flex='0 0 auto' alignSelf='center' height='100%'>
            <Button onClick={deleteStopHandler} color='tertiary' large disabled={isDragDisabled}>
              <DeleteIcon size='large' />
            </Button>
          </Box>
        )}
      </Box>
    </DraggableListItem>
  )
}
